/**
 * Aromely pricing runtime — picks and paints prices computed by snippets/aromely-price.liquid.
 *
 * No discount math lives here: every figure comes from a data-price-map written by Liquid
 * for both promotion states. This module only (1) picks the entry for the browser state
 * (html[data-promo], set by snippets/aromely-promo-state.liquid), a variant and a plan,
 * (2) multiplies a per-unit figure by a quantity, (3) formats with the shop money format.
 */
import { formatMoney as formatMinorUnits } from '@theme/money-formatting';

const ROOT = document.documentElement;
const listeners = new Set();
const mapCache = new WeakMap();
let lastState = promoState();

/** @returns {'on'|'off'} */
export function promoState() {
  return ROOT.dataset.promo === 'on' ? 'on' : 'off';
}

/** Calls `callback(state)` after every real flip of html[data-promo]. */
export function onPromoChange(callback, { signal } = {}) {
  listeners.add(callback);
  const off = () => listeners.delete(callback);
  signal?.addEventListener('abort', off, { once: true });
  return off;
}

/** Parsed data-price-map of `el` (cached per element and raw string, so call it at paint time). */
export function readMap(el) {
  const raw = el && el.dataset ? el.dataset.priceMap : '';
  if (!raw) return null;
  const hit = mapCache.get(el);
  if (hit && hit.raw === raw) return hit.map;
  let map = null;
  try { map = JSON.parse(raw); } catch { map = null; }
  mapCache.set(el, { raw, map });
  return map;
}

export function pickLine(map, state = promoState()) {
  return (map && (map[state] || map.off)) || null;
}

export function pickOption(map, variantId, planId = '', state = promoState()) {
  const variant = map && map.v && map.v[String(variantId)];
  if (!variant) return null;
  const option = (planId && variant.p && variant.p[String(planId)]) || variant.o;
  return pickLine(option, state);
}

export function formatMoney(cents, format, currency) {
  return formatMinorUnits(Math.round(Number(cents) || 0), String(format || '${{amount}}'), String(currency || 'USD'));
}

export function setVisible(el, visible, mode = 'class', displayClass = '') {
  if (!el) return;
  if (mode === 'attr') { el.hidden = !visible; return; }
  el.classList.toggle('hidden', !visible);
  if (displayClass) el.classList.toggle(displayClass, visible);
}

/** Literal, all-occurrence substitution (like Liquid `replace`); `$&`-style patterns stay literal. */
const sub = (text, token, value) => text.replaceAll(token, () => value);

export function fillTemplate(tpl, e) {
  let out = String(tpl);
  out = sub(out, '[price]', e.F ?? '');
  out = sub(out, '[compare]', e.C ?? '');
  out = sub(out, '[pct]', String(e.b ?? 0));
  out = sub(out, '[label]', e.l ?? '');
  out = sub(out, '[recurring]', e.R ?? '');
  return out;
}

export function thenText(map, entry, quantity = 1, format, currency) {
  if (!map || !map.t || !entry || entry.r == null) return '';
  const q = Math.max(1, Number(quantity) || 1);
  return sub(String(map.t), '[price]', q > 1 ? formatMoney(entry.r * q, format, currency) : entry.R);
}

export function paintReactive({ price, compare, badge }, entry, { quantity = 1, format, currency } = {}) {
  if (!entry) return;
  const q = Math.max(1, Number(quantity) || 1);
  const strike = entry.c > entry.f;
  if (price) price.textContent = q > 1 ? formatMoney(entry.f * q, format, currency) : entry.F;
  if (compare) {
    compare.textContent = strike ? (q > 1 ? formatMoney(entry.c * q, format, currency) : entry.C) : '';
    setVisible(compare, strike, 'class');
  }
  if (badge) {
    const show = strike && entry.b > 0;
    badge.textContent = show ? entry.l : '';
    // .inline-flex and .hidden have equal specificity: never leave both on.
    setVisible(badge, show, 'class', 'inline-flex');
  }
}

/* ── module-owned looks ─────────────────────────────────────────────── */
const LINE = {
  sale: 'font-normal text-[#0c0a09]',
  strike: 'font-normal text-[#78716c] line-through',
  single: 'font-medium text-[#0c0a09]',
  badge: 'inline-flex items-center shrink-0 h-4.5 px-1.5 rounded-sm bg-[#0c0a09] text-white text-[10px] font-medium leading-[1.1] tracking-[0.1px]',
};
function node(tag, className, text) {
  const el = document.createElement(tag);
  if (className) el.className = className;
  el.textContent = text;
  return el;
}
const LOOKS = {
  /** snippets/aromely-price.liquid static line — must equal its Liquid markup (SPEC §C.5). */
  line(el, e) {
    const kids = [];
    if (e.c > e.f) {
      kids.push(node('span', LINE.sale, e.F), node('s', LINE.strike, e.C));
      if (el.dataset.priceBadge !== '0' && e.b > 0) kids.push(node('span', LINE.badge, e.l));
    } else {
      kids.push(node('span', LINE.single, e.F));
    }
    el.replaceChildren(...kids);
  },
  /** Plain text: data-price-tpl, else data-price-field (default F). */
  text(el, e) {
    const tpl = el.dataset.priceTpl;
    el.textContent = tpl != null ? fillTemplate(tpl, e) : (e[el.dataset.priceField || 'F'] ?? '');
  },
  /** "final" text node followed by an optional <s> (promo-recent mini card). */
  pair(el, e) {
    const kids = [document.createTextNode(e.F)];
    if (e.c > e.f) kids.push(node('s', '', e.C));
    el.replaceChildren(...kids);
  },
  /** Element shown only with a badge; text into [data-price-text] (cross-sell corner). */
  badge(el, e) {
    const show = e.b > 0;
    (el.querySelector('[data-price-text]') || el).textContent = show ? e.l : '';
    el.hidden = !show;
  },
};

/**
 * Repaints module-owned looks under (and including) `root` whose data-price-state differs
 * from `state`. Host-owned looks (pdp, none) found stale get a bubbling
 * `aromely:price-stale` event so their host repaints them (e.g. after an in-place morph);
 * a flip skips that event because hosts already hear it through onPromoChange.
 */
export function repaint(root = document, state = promoState(), { notifyHosts = true } = {}) {
  const list = [];
  if (root.nodeType === 1 && root.matches('[data-price-map][data-price-look]')) list.push(root);
  if (root.querySelectorAll) list.push(...root.querySelectorAll('[data-price-map][data-price-look]'));
  for (const el of list) {
    if (el.dataset.priceState === state) continue;
    const paint = LOOKS[el.dataset.priceLook];
    if (!paint) {
      if (notifyHosts) el.dispatchEvent(new CustomEvent('aromely:price-stale', { bubbles: true, detail: { state } }));
      continue;
    }
    const entry = pickLine(readMap(el), state);
    if (!entry) continue;
    paint(el, entry);
    el.dataset.priceState = state;
  }
}

function flip() {
  const state = promoState();
  if (state === lastState) return;
  lastState = state;
  repaint(document, state, { notifyHosts: false });
  for (const cb of [...listeners]) {
    try { cb(state); } catch (error) { console.error('aromely-pricing listener failed', error); }
  }
}

new MutationObserver(flip).observe(ROOT, { attributes: true, attributeFilter: ['data-promo'] });
// Inserted fragments, and fragments morphed in place (attributes or children rewritten by
// section rendering / Horizon morph), are brought to the browser state.
new MutationObserver((records) => {
  const state = promoState();
  const touched = new Set();
  for (const record of records) {
    if (record.type === 'attributes') { touched.add(record.target); continue; }
    const holder = record.target.closest ? record.target.closest('[data-price-map]') : null;
    if (holder) touched.add(holder);
    for (const added of record.addedNodes) if (added.nodeType === 1) touched.add(added);
  }
  for (const el of touched) if (el.isConnected) repaint(el, state);
}).observe(ROOT, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-price-state', 'data-price-map'] });
repaint(document, lastState);

window.AromelyPricing = { promoState, pickLine, pickOption, readMap, formatMoney, repaint, onPromoChange };
